import { getLocale, getT, getUnitSystem } from '@/i18n';
/**
 * Pozíció a GRUNDO saját, Capacitoron át elérhető natív helyforrásából.
 *
 * A Capacitor WebView `navigator.geolocation` API-ja nem alkalmas megbízható
 * háttérmérésre. A plugin ezzel szemben a tényleges GRUNDO alkalmazás natív
 * jogosultságát kéri. iOS-en Core Location, Androidon egy location típusú
 * foreground service teszi tartós sorba a pontokat, amelyeket az ébredő
 * JavaScript átvesz.
 */

import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import type { ActivityType } from '@/types';
import { liveActivityEnabled } from './liveActivity';
import {
  TrackingError,
  type PositionActivityState,
  type PositionHandlers,
  type PositionSource,
} from './types';

interface NativeLocation {
  lat: number;
  lng: number;
  t: number;
  accuracy: number;
  elevation?: number;
  speed?: number;
}

/**
 * A natív helyzet-diagnosztika válasza.
 *
 * A mezők KÖZÖSEK a két platform között (`BackgroundLocationPlugin.swift`,
 * `BackgroundLocationPlugin.java`), hogy a tesztelői panel egyetlen kódot
 * rajzoljon. Ami csak az egyiken értelmes, az opcionális — a `platform`
 * mezőből derül ki, melyiket kell olvasni.
 */
export interface NativeLocationDiagnostics {
  platform: 'ios' | 'android';
  /** `notDetermined` | `whenInUse` | `always` | `denied` | `restricted` | `unknown` */
  authorization: string;
  /** `full` | `reduced` */
  accuracy: string;
  updating: boolean;
  /**
   * iOS (#80): fut-e a háttérindítás utáni folytatás (`GrundoRecordingResumer`)
   * — az app kilövése után az iOS újraindította, és a mérés tovább ment.
   */
  resumerActive?: boolean;
  /** iOS (#80): a tartós jelző szerint van-e félbe nem hagyott rögzítés. */
  resumeMarker?: boolean;
  /** iOS: létrejött-e már a rögzítés (háttér-GPS) managere. */
  recordingManagerCreated?: boolean;
  /**
   * iOS: a fő szálon jött-e létre a két `CLLocationManager`.
   *
   * ⚠️ HA EZ VALAHA `false`, MEGVAN A BLOKKOLÓ HIBA OKA. A Core Location a
   * delegate-hívásait a manager létrehozásának szálán, annak futókörén
   * kézbesíti; a Capacitor viszont minden plugin-hívást háttérsoron futtat.
   * Egy ott létrehozott manager némán elnyelheti a választ — a térkép nem áll
   * középre, a recenter gomb meg sem jelenik.
   */
  oneShotManagerOnMainThread?: boolean;
  recordingManagerOnMainThread?: boolean;
  pendingStart?: boolean;
  /** iOS: hány `getCurrentPosition` vár még válaszra a natív oldalon. */
  pendingPositionCalls?: number;
  awaitingPositionAuthorization?: boolean;
  /** iOS: fut-e épp az egyszeri fix mérése (a JS lejárta után is futhat). */
  oneShotUpdating?: boolean;
  /** iOS: van-e natívan eltárolt, újrahasználható fix. */
  hasCachedFix?: boolean;
  alwaysRequested?: boolean;
  pendingLocations?: number;
  persistedBatches?: number;
  backgrounded?: boolean;
  /** Android: él-e a szolgáltatás szakadatlanul (nem kilövés utáni újraindítás). */
  serviceAlive?: boolean;
  lastFixAgeMs?: number;
  lastOneShotFixAgeMs?: number;
  lastOneShotRequestAgeMs?: number;
  lastError?: string;
  lastErrorAgeMs?: number;
  /** Android: a három tényleges engedély külön — ott nincs `notDetermined`. */
  fineLocation?: boolean;
  coarseLocation?: boolean;
  backgroundLocation?: boolean;
  notifications?: boolean;
  locationServices?: boolean;
  /**
   * iOS: az elindult / megszakított UIKit-mozdulatfelismerők naplója, EGY
   * szövegben (`GRUNDOBridgeViewController.gestureLogSummary`). A Befejezés
   * gomb `pointercancel`-jének okát keressük vele.
   */
  gestureLog?: string;
}

interface BackgroundLocationPlugin {
  start(options: {
    activityType: ActivityType;
    locale: 'hu' | 'en';
    units: 'metric' | 'imperial';
    activityState?: PositionActivityState;
    liveActivityEnabled: boolean;
    /** `false` esetén a natív oldal eldobja a leragadt lemezes sort induláskor. */
    resume: boolean;
  }): Promise<{
    permission: 'granted' | 'prompt';
    backgroundPermission?: 'granted' | 'not_granted';
    notificationPermission?: 'granted' | 'not_granted';
  }>;
  stop(): Promise<void>;
  /**
   * Egyszeri fix a térkép középre igazításához — NEM méréshez.
   *
   * ⚠️ Ez azért létezik, hogy a natív app SOHA ne hívja a WebView
   * `navigator.geolocation` API-ját. Az ugyanis a rendszer engedélykérdése
   * mellé egy MÁSODIK, oldal-szintű kérdést is felhoz, amit a WebKit a saját
   * processzünkben rajzol — iOS-en angolul, a Capacitor kiszolgálójának
   * nevével („localhost would like to use your current location”, mérve
   * 2026-09-09). A natív út ezt megkerüli.
   */
  getCurrentPosition(options?: {
    locale?: 'hu' | 'en';
    /**
     * Ennél frissebb, natívan eltárolt fix elfogadható — ilyenkor a válasz
     * AZONNALI, nem indul új mérés.
     *
     * ⚠️ MÉRVE (iPhone, 2026-09-15): a friss engedély utáni ELSŐ fix ~20 000 ms
     * volt, a második 16 ms. A gyorsítótár nem kényelem, hanem ez ellen véd.
     */
    maxAgeMs?: number;
  }): Promise<{ lat: number; lng: number; accuracy: number; t: number }>;
  /**
   * A natív helyzet-gépezet állapota — CSAK diagnosztikához.
   *
   * ⚠️ Ez az EGYETLEN út, amin iOS-en látszik, mit csinál a Core Location: a
   * WebView `navigator.permissions` API-ja a helyzet-engedélyről semmit nem
   * árul el, Windowsról pedig Safari Web Inspector sincs.
   *
   * ⚠️ HELYADAT NINCS BENNE — állapotnév, darabszám és kor ezredmásodpercben.
   */
  getDiagnostics(): Promise<NativeLocationDiagnostics>;
  syncActivity(options: PositionActivityState & { locale: 'hu' | 'en'; units: 'metric' | 'imperial' }): Promise<void>;
  /**
   * ÉLŐ KÖZVETÍTÉS: a natív réteg innentől MINDEN rögzítési mintát (szünet
   * alatt nem) ~3 másodpercenként elküld az `endpoint`-ra az `X-Live-Key`
   * fejléccel — előtérben ÉS zárt képernyőnél is. Üres objektum: leállítja.
   * Lásd `server/src/routes/live.ts`.
   */
  /**
   * `intervalMs`: a küldés üteme (1.25.3+). Régebbi natív build figyelmen
   * kívül hagyja, és a saját 3 másodpercével küld.
   */
  configureLive(options: { endpoint?: string; key?: string; intervalMs?: number }): Promise<void>;
  drain(): Promise<{ locations: NativeLocation[] }>;
  /** iOS (#80): a háttér-folytatás jelzőjének törlése — lásd `clearNativeResume`. Androidon nincs. */
  clearResume(): Promise<void>;
  addListener(eventName: 'location', listenerFunc: (location: NativeLocation) => void): Promise<PluginListenerHandle>;
  addListener(
    eventName: 'error',
    listenerFunc: (error: { code?: string; message?: string }) => void,
  ): Promise<PluginListenerHandle>;
}

const BackgroundLocation = registerPlugin<BackgroundLocationPlugin>('BackgroundLocation');

/**
 * A natív élő mintaküldés be- vagy kikapcsolása. `false`, ha a natív réteg
 * ezt nem ismeri (régebbi build) — a hívó ilyenkor a webes közvetítésre
 * marad.
 */
export async function configureNativeLive(
  config: { endpoint: string; key: string; intervalMs?: number } | null,
): Promise<boolean> {
  try {
    await BackgroundLocation.configureLive(config ?? {});
    return true;
  } catch {
    return false;
  }
}

/**
 * Egyszeri natív fix — a térkép kiindulási középpontjához.
 *
 * ⚠️ NEM a méréshez való: az a `NativePositionSource` dolga. Ez a hívás
 * kizárólag a `navigator.geolocation` KIVÁLTÁSÁRA létezik natív appban, mert
 * a WebView geolocation API-ja egy fölösleges, oldal-szintű engedélykérdést
 * is felhoz (lásd a `getCurrentPosition` fejlécét fent).
 *
 * A hívó dolga eldönteni, hogy natív-e a platform — itt nem ágazunk el, hogy
 * a modul tesztelhető maradjon.
 */
export async function nativeCurrentPosition(maxAgeMs?: number): Promise<{
  lat: number;
  lng: number;
  accuracyM: number;
  at: number;
}> {
  const fix = await BackgroundLocation.getCurrentPosition({ maxAgeMs, locale: getLocale() }).catch((error: unknown) => { throw toTrackingError(error); });
  return {
    lat: fix.lat,
    lng: fix.lng,
    accuracyM: Number.isFinite(fix.accuracy) ? fix.accuracy : 99_999,
    at: Number.isFinite(fix.t) && fix.t > 0 ? fix.t : Date.now(),
  };
}

/**
 * A natív helyzet-gépezet állapota — CSAK a tesztelői diagnosztikának.
 *
 * A hívó dolga eldönteni, hogy natív-e a platform; itt nem ágazunk el, hogy a
 * modul tesztelhető maradjon (ugyanaz az elv, mint a `nativeCurrentPosition`-nél).
 */
/**
 * A félbehagyott rögzítést a JS eldobta: a natív háttér-folytatás (#80) se
 * induljon el miatta többé. Régi natív build és Android: nincs ilyen metódus —
 * csendben semmi.
 */
export async function clearNativeResume(): Promise<void> {
  await BackgroundLocation.clearResume().catch(() => undefined);
}

/**
 * A natív lemezes sor kiürítése a visszaállítási döntés ELŐTT (lásd
 * `catchUpRun`). Hiba vagy régi natív build: üres lista.
 */
export async function drainNativeQueue(): Promise<NativeLocation[]> {
  const queued = await BackgroundLocation.drain().catch(() => null);
  return queued?.locations ?? [];
}

export async function nativeLocationDiagnostics(): Promise<NativeLocationDiagnostics> {
  return BackgroundLocation.getDiagnostics();
}

export class NativePositionSource implements PositionSource {
  readonly name = 'native';
  private backgroundPermissionGranted = false;
  get supportsBackground(): boolean {
    return this.backgroundPermissionGranted;
  }
  readonly ordered = false;

  private locationListener: PluginListenerHandle | null = null;
  private errorListener: PluginListenerHandle | null = null;
  private visibilityListener: (() => void) | null = null;
  private handlers: PositionHandlers | null = null;
  /**
   * A natív esemény és a tartós sor drainje ébredéskor versenyezhet.
   * Amíg a drain fut, az élő eseményeket itt tartjuk, majd a teljes köteget
   * időrendben adjuk át. Enélkül egy friss esemény előreszaladhatott, és a
   * korábbi háttérpontokat a régi monotónia-őr mind eldobta.
   */
  private drainPromise: Promise<void> | null = null;
  private awaitingForegroundDrain = false;
  traceSink: PositionSource['traceSink'];
  private bufferedLocations: NativeLocation[] = [];
  /**
   * KÉTGENERÁCIÓS, KORLÁTOS duplikátumszűrő.
   *
   * ⚠️ Ez memóriaszivárgás javítása. Korábban egyetlen, sosem ürülő halmaz
   * gyűjtötte az aktivitás MINDEN időbélyegét — egy kétórás bringázás után
   * több tízezer bejegyzés, kizárólag azért, hogy egy másodperces
   * drain/esemény versenyt lekezeljünk.
   *
   * A duplikátum kizárólag az ébredéskori drain és az élő esemény
   * ÁTFEDÉSÉBŐL keletkezhet, ami másodperces nagyságrend. Két generáció
   * (`recent` + `older`) tehát bőven fedi: amint a friss halmaz megtelik,
   * lecsúszik az öregbe, és onnan esik ki. A védelem így legalább
   * `DEDUP_WINDOW`, legfeljebb `2 × DEDUP_WINDOW` mintányi múltra érvényes.
   *
   * A második védelmi vonal amúgy is megvan: a `recorder.evaluate()` az
   * azonos időbélyegű mintát `not_newer` okkal elutasítja.
   */
  private recentTimestamps = new Set<number>();
  private olderTimestamps = new Set<number>();

  async start(
    handlers: PositionHandlers,
    activityType: ActivityType = 'run',
    activityState?: PositionActivityState,
    resume = false,
  ): Promise<void> {
    // Egy új WebView ugyanahhoz a már futó natív helyméréshez
    // kapcsolódik vissza. Itt TILOS a natív szolgáltatást leállítani: az
    // átmeneti WebView-újraindulás különben valódi GPS-lyukat okozna.
    await this.detach();
    this.handlers = handlers;
    this.locationListener = await BackgroundLocation.addListener('location', (location) => {
      if (
        this.drainPromise !== null ||
        this.awaitingForegroundDrain ||
        document.visibilityState !== 'visible'
      ) this.bufferedLocations.push(location);
      else this.deliver(location);
    });
    this.errorListener = await BackgroundLocation.addListener('error', (error) => {
      handlers.onError(toTrackingError(error));
    });
    this.visibilityListener = () => {
      if (document.visibilityState !== 'visible') {
        this.awaitingForegroundDrain = true;
        return;
      }
      void this.drain();
    };
    document.addEventListener('visibilitychange', this.visibilityListener);

    try {
      const status = await BackgroundLocation.start({
        activityType,
        locale: getLocale(),
        units: getUnitSystem(),
        activityState,
        liveActivityEnabled: liveActivityEnabled(),
        resume,
      });
      this.backgroundPermissionGranted = status.backgroundPermission === 'granted';
      await this.drain();
    } catch (error) {
      await this.stop();
      throw toTrackingError(error);
    }
  }

  async syncActivity(state: PositionActivityState): Promise<void> {
    await BackgroundLocation.syncActivity({ ...state, locale: getLocale(), units: getUnitSystem() });
  }

  async stop(): Promise<void> {
    if (this.handlers !== null) await this.drain();
    await this.detach();
    this.backgroundPermissionGranted = false;
    await BackgroundLocation.stop().catch(() => undefined);
    this.awaitingForegroundDrain = false;
    this.bufferedLocations = [];
    this.recentTimestamps.clear();
    this.olderTimestamps.clear();
  }

  async detach(): Promise<void> {
    await this.locationListener?.remove();
    await this.errorListener?.remove();
    if (this.visibilityListener !== null) {
      document.removeEventListener('visibilitychange', this.visibilityListener);
    }
    this.locationListener = null;
    this.errorListener = null;
    this.visibilityListener = null;
    this.handlers = null;
  }

  flushPending(): Promise<void> {
    return this.handlers === null ? Promise.resolve() : this.drain();
  }

  hasPendingSamples(): boolean {
    return this.drainPromise !== null || this.awaitingForegroundDrain || this.bufferedLocations.length > 0;
  }

  private drain(): Promise<void> {
    // A visibility, a start és a stop ugyanarra a folyamatban lévő drainre
    // vár. Ha a stop csak azonnal visszatérne, leválasztaná a handlert,
    // mielőtt a már kiolvasott háttérpontok eljutnak a recorderhez.
    if (this.drainPromise !== null) return this.drainPromise;

    this.drainPromise = (async () => {
      try {
        const queued = await BackgroundLocation.drain().catch(() => null);
        const buffered = this.bufferedLocations.splice(0);
        const locations = [
          ...(queued?.locations ?? []),
          ...buffered,
        ].sort((a, b) => a.t - b.t);
        // Csak a tartalmas ürítést naplózzuk: az üres sor nem érdekes, és a
        // háttérben ötmásodpercenként futó ellenőrzés elárasztaná a naplót.
        if (locations.length > 0) {
          this.traceSink?.('drain', {
            q: queued?.locations.length ?? -1,
            b: buffered.length,
            first: locations[0]!.t,
            last: locations[locations.length - 1]!.t,
          });
        }
        for (const location of locations) this.deliver(location);
      } finally {
        // A fenti szinkron feldolgozás alatt nem futhat JS callback, de a
        // `finally` védi azt az esetet is, ha a plugin-válasz feldolgozása
        // később aszinkron lépéssel bővülne.
        const remaining = this.bufferedLocations.splice(0).sort((a, b) => a.t - b.t);
        for (const location of remaining) this.deliver(location);
        this.awaitingForegroundDrain = false;
        this.drainPromise = null;
      }
    })();
    return this.drainPromise;
  }

  private deliver(location: NativeLocation): void {
    if (this.handlers === null || this.alreadyDelivered(location.t)) return;
    this.remember(location.t);
    this.handlers.onSample(location);
  }

  private alreadyDelivered(t: number): boolean {
    return this.recentTimestamps.has(t) || this.olderTimestamps.has(t);
  }

  private remember(t: number): void {
    this.recentTimestamps.add(t);
    if (this.recentTimestamps.size < DEDUP_WINDOW) return;
    this.olderTimestamps = this.recentTimestamps;
    this.recentTimestamps = new Set();
  }
}

/**
 * Ennyi minta után csúszik generációt a duplikátumszűrő.
 *
 * 600 minta ~10 perc 1 Hz-en — nagyságrendekkel több, mint amennyi drain/
 * esemény átfedés valaha keletkezhet, és mégis állandó memóriahasználat.
 */
const DEDUP_WINDOW = 600;

function toTrackingError(error: unknown): TrackingError {
  const code = typeof error === 'object' && error !== null && 'code' in error
    ? String(error.code)
    : '';
  const message = error instanceof Error
    ? error.message
    : typeof error === 'object' && error !== null && 'message' in error
      ? String(error.message)
      : '';

  if (code === 'permission_denied' || message.includes('helyhozzáférés')) {
    return new TrackingError(
      'permission_denied',
      getLocale() === 'en' ? getT().nativeLocationErrors.permission : message || getT().nativeLocationErrors.permission,
    );
  }
  if (code === 'location_disabled') {
    return new TrackingError(
      'unavailable',
      getLocale() === 'en' ? getT().nativeLocationErrors.disabled : message || getT().nativeLocationErrors.disabled,
    );
  }
  return new TrackingError('unavailable', getLocale() === 'en' ? getT().nativeLocationErrors.failed : message || getT().nativeLocationErrors.failed);
}
